import { canModifyRepoItem } from "./canModifyRepoItem";

export const NON_DOWNLOADABLE_FILE_TYPES = [
  "webpage",
  "youtube_audio",
  "url",
  "video",
  "text",
  "qa",
];

export const isDownloadableRepoItem = (item) =>
  item?.type === "file" &&
  !NON_DOWNLOADABLE_FILE_TYPES.includes(
    String(item?.fileType || "").toLowerCase(),
  );

export const isChatEligibleRepoItem = (item) => {
  if (item?.type === "folder") {
    if (item?.isGroupRoot || item?.isPersonalSpace) return false;
    if (String(item?.fileStatus || "1") !== "1") return false;
    return true;
  }
  if (!isDownloadableRepoItem(item)) return false;
  return String(item?.approvalStatus || "").toLowerCase() !== "rejected";
};

export const getRepoItemActionState = (
  item,
  role,
  repoView,
  selectionCount = 1,
  items = [],
) => {
  const isGroupView = repoView === "group";
  const isGroupUser = isGroupView && role !== "admin";
  const isGroupAdmin = isGroupView && role === "admin";
  const isPersonalRepoView = repoView === "personal";
  const isGlobalRepoView = repoView === "global";
  const isNormalUser = role !== "admin";

  const isPersonalDeletableItem =
    item.isPersonalSpace === true ||
    (!item.isPersonalSpace &&
      item.ownerType !== "admin" &&
      item.mode !== "global" &&
      item.mode !== "group");

  const approvalStatus = String(item?.approvalStatus || "").toLowerCase();
  const isApprovedFile = item?.type === "file" && approvalStatus === "approved";
  const isDuplicateFile =
    item?.type === "file" && approvalStatus === "duplicate";

  const showChangeStatus =
    item.type === "file" &&
    !item.isPersonalSpace &&
    ((repoView === "global" && role === "admin") ||
      (repoView === "personal" && role !== "admin" && isApprovedFile) ||
      (repoView === "group" && role === "admin"));

  const hasDownloadableChildren = (folderItem) => {
    if (folderItem?.type !== "folder") return false;
    return items.some(
      (child) =>
        String(child.parentId) === String(folderItem.id) &&
        isDownloadableRepoItem(child),
    );
  };

  const canDownloadItem =
    item?.type === "folder"
      ? isGlobalRepoView && isNormalUser
        ? hasDownloadableChildren(item)
        : true
      : isDownloadableRepoItem(item);

  return {
    canDownload: canDownloadItem,
    canChat:
      isChatEligibleRepoItem(item) &&
      role !== "admin" &&
      repoView !== "personal_space",
    canChangeStatus:
      !isGroupUser &&
      item.type === "file" &&
      (item.isPersonalSpace || showChangeStatus),
    canRenameOrMove:
      !isGroupUser &&
      (isGroupAdmin ? true : canModifyRepoItem(item, role)) &&
      selectionCount === 1,
    canDelete:
      !isGroupView && (item.isPersonalSpace || isPersonalDeletableItem),
    canRestore: item.isPersonalSpace && item.fileStatus === "0",
    isPersonalRepoView,
    isApprovedFile,
    isDuplicateFile,
  };
};

export const canDownloadSelectedItems = (selectedIds, items = []) =>
  collectDownloadableFiles(selectedIds, items);

export const collectDownloadableFiles = (selectedIds, items = []) => {
  const selectedItems = items.filter((item) => selectedIds.has(item.id));
  const downloadCandidates = [];
  const visitedIds = new Set();

  const walk = (node, pathSegments = []) => {
    if (!node) return;

    if (node.type === "folder") {
      const nextPath = [...pathSegments, node.name];
      const children = items.filter(
        (child) => String(child.parentId) === String(node.id),
      );
      children.forEach((child) => walk(child, nextPath));
      return;
    }

    if (!isDownloadableRepoItem(node)) return;

    const uniqueKey = String(node.id);
    if (visitedIds.has(uniqueKey)) return;

    visitedIds.add(uniqueKey);
    downloadCandidates.push({
      ...node,
      entryPath: [...pathSegments, node.name].join("/"),
    });
  };

  selectedItems.forEach((item) => {
    if (item.type === "folder") {
      walk(item, []);
      return;
    }

    if (isDownloadableRepoItem(item)) {
      const uniqueKey = String(item.id);
      if (!visitedIds.has(uniqueKey)) {
        visitedIds.add(uniqueKey);
        downloadCandidates.push({
          ...item,
          entryPath: item.name,
        });
      }
    }
  });

  return downloadCandidates;
};
